import React, { useState, useCallback, useRef, useMemo } from 'react';
import {
  Dialog,
  Box,
  IconButton,
  LinearProgress,
  Fade,
} from '@mui/material';
import { ArrowBack, Close } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import QuizQuestionComponent from './QuizQuestion';
import QuizResultScreen from './QuizResultScreen';
import { QuizCelebration } from './components';
import { QUIZ_QUESTIONS, getEnhancedRecommendations } from './quizData';
import { selectInsights } from './utils';
import { useQuizInsights } from './hooks';
import type { QuizAnswers } from './quizTypes';
import { useLoginDialog } from '../../contexts/LoginDialogContext';
import analyticsService from '../../services/analytics';
import { useOnboardingChecklistSafe } from '../../contexts/OnboardingChecklistContext';

interface DiscoveryQuizModalProps {
  open: boolean;
  onClose: () => void;
  source?: string;
}

const TOTAL_STEPS = QUIZ_QUESTIONS.length; // 5 questions
const RESULT_STEP = TOTAL_STEPS; // index 5 = result screen
const COMPLETED_KEY = 'discovery_quiz_completed';

const DiscoveryQuizModal: React.FC<DiscoveryQuizModalProps> = ({ open, onClose, source = 'unknown' }) => {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState<QuizAnswers>({});
  const [showCelebration, setShowCelebration] = useState(false);
  const navigate = useNavigate();
  const { openLoginDialog } = useLoginDialog();
  const onboardingContext = useOnboardingChecklistSafe();
  const autoAdvanceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // -- helpers ---------------------------------------------------------------

  const currentQuestion = step < TOTAL_STEPS ? QUIZ_QUESTIONS[step] : null;
  const progress = ((Math.min(step, TOTAL_STEPS)) / TOTAL_STEPS) * 100;

  const currentValue = currentQuestion
    ? answers[currentQuestion.id as keyof QuizAnswers]
    : undefined;

  // -- result data -----------------------------------------------------------

  const result = step === RESULT_STEP ? getEnhancedRecommendations(answers) : null;

  // -- insight data ----------------------------------------------------------

  // Compute insight configs when we reach the result step
  const insightConfigs = useMemo(() => {
    if (step === RESULT_STEP) {
      return selectInsights(answers);
    }
    return null;
  }, [step, answers]);

  // Fetch live data for insights (only enabled when at result step)
  const { insights, isLoading: insightsLoading, hasLiveData } = useQuizInsights(
    insightConfigs,
    step === RESULT_STEP
  );

  // Handle celebration completion
  const handleCelebrationComplete = useCallback(() => {
    setShowCelebration(false);

    // Log insights shown analytics
    if (insights.length > 0) {
      analyticsService.trackQuizInsightsShown(
        insights.map((i) => i.config.type),
        insights.map((i) => i.config.symbol),
        hasLiveData
      );
    }
  }, [insights, hasLiveData]);

  // -- handlers --------------------------------------------------------------

  const handleSelect = useCallback(
    (choiceId: string) => {
      if (!currentQuestion) return;
      const qid = currentQuestion.id as keyof QuizAnswers;

      if (currentQuestion.multiSelect) {
        // Toggle in array
        const prev = (answers[qid] as string[] | undefined) ?? [];
        const next = prev.includes(choiceId)
          ? prev.filter((id) => id !== choiceId)
          : [...prev, choiceId];
        setAnswers((a) => ({ ...a, [qid]: next }));
      } else {
        // Single-select: set value, auto-advance after short delay
        setAnswers((a) => ({ ...a, [qid]: choiceId }));

        analyticsService.trackQuizStepCompleted(step, currentQuestion.id, choiceId);

        if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current);
        autoAdvanceTimer.current = setTimeout(() => {
          setStep((s) => s + 1);
        }, 400);
      }
    },
    [currentQuestion, answers, step],
  );

  const handleMultiContinue = useCallback(() => {
    if (!currentQuestion) return;

    analyticsService.trackQuizStepCompleted(step, currentQuestion.id, answers.needs ?? []);

    setStep((s) => s + 1);
  }, [currentQuestion, step, answers.needs]);

  const handleBack = useCallback(() => {
    if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current);
    setStep((s) => Math.max(0, s - 1));
  }, []);

  const handleClose = useCallback(() => {
    if (autoAdvanceTimer.current) clearTimeout(autoAdvanceTimer.current);

    analyticsService.trackQuizAbandoned(step, currentQuestion?.id ?? 'result');

    // Reset for next open
    setStep(0);
    setAnswers({});
    setShowCelebration(false);
    onClose();
  }, [step, currentQuestion, onClose]);

  const handleCreateAccount = useCallback(() => {
    localStorage.setItem('discovery_quiz_answers', JSON.stringify(answers));

    // Store the primary recommendation route for post-login navigation
    const primaryRoute = result?.recommendations[0]?.route;
    if (primaryRoute) {
      localStorage.setItem('discovery_quiz_redirect', primaryRoute);
    }

    analyticsService.trackQuizCtaClicked('save_my_plan', primaryRoute);

    onClose();
    openLoginDialog('discovery_quiz');
  }, [answers, result, onClose, openLoginDialog]);

  const handleExplore = useCallback(
    (route: string) => {
      analyticsService.trackQuizCtaClicked('explore_first', route);

      // Reset for next open
      setStep(0);
      setAnswers({});
      setShowCelebration(false);
      onClose();
      navigate(route);
    },
    [onClose, navigate],
  );

  // Fire completion event when reaching result screen and show celebration
  const prevStepRef = useRef(step);
  if (step === RESULT_STEP && prevStepRef.current !== RESULT_STEP) {
    analyticsService.trackQuizCompleted(answers);
    // Mark onboarding task as complete
    onboardingContext?.completeTask('quizCompleted');
    // Mark as completed so triggers don't re-show
    try { localStorage.setItem(COMPLETED_KEY, 'true'); } catch { /* noop */ }
    // Show celebration transition
    setShowCelebration(true);
  }
  prevStepRef.current = step;

  // Fire open event
  const openFiredRef = useRef(false);
  if (open && !openFiredRef.current) {
    analyticsService.trackQuizOpened(source);
    openFiredRef.current = true;
  }
  if (!open) openFiredRef.current = false;

  // -- render ----------------------------------------------------------------

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen
      PaperProps={{
        sx: {
          bgcolor: 'background.default',
        },
      }}
    >
      {/* Full screen container with centered content */}
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        {/* Header: progress bar + navigation */}
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 10,
            bgcolor: 'background.default',
          }}
        >
          {/* Progress bar */}
          <LinearProgress
            variant="determinate"
            value={progress}
            sx={{
              height: 4,
              bgcolor: 'divider',
              '& .MuiLinearProgress-bar': {
                bgcolor: 'text.primary',
                transition: 'transform 0.4s ease',
              },
            }}
          />

          {/* Back + close buttons */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              px: 2,
              py: 1.5,
              maxWidth: 600,
              mx: 'auto',
              width: '100%',
            }}
          >
            {step > 0 && step <= TOTAL_STEPS ? (
              <IconButton onClick={handleBack} size="small" sx={{ color: 'text.secondary' }}>
                <ArrowBack fontSize="small" />
              </IconButton>
            ) : (
              <Box sx={{ width: 34 }} />
            )}

            <IconButton onClick={handleClose} size="small" sx={{ color: 'text.secondary' }}>
              <Close fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Scrollable content area */}
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            px: { xs: 3, sm: 4 },
            pb: 4,
            pt: 2,
          }}
        >
          {/* Centered content container */}
          <Box sx={{ maxWidth: 560, mx: 'auto' }}>
            {/* Questions (0-4) */}
            {currentQuestion && (
              <Fade in key={currentQuestion.id} timeout={300}>
                <div>
                  <QuizQuestionComponent
                    question={currentQuestion}
                    value={currentValue}
                    onSelect={handleSelect}
                    onContinue={handleMultiContinue}
                  />
                </div>
              </Fade>
            )}

            {/* Celebration transition (before showing results) */}
            {step === RESULT_STEP && showCelebration && (
              <QuizCelebration
                visible={showCelebration}
                onComplete={handleCelebrationComplete}
                minDisplayTime={1500}
                isDataLoading={insightsLoading}
              />
            )}

            {/* Result screen (step 5, after celebration) */}
            {result && !showCelebration && (
              <Fade in timeout={300}>
                <div>
                  <QuizResultScreen
                    result={result}
                    insights={insights}
                    insightsLoading={insightsLoading}
                    hasLiveData={hasLiveData}
                    onCreateAccount={handleCreateAccount}
                    onExplore={handleExplore}
                  />
                </div>
              </Fade>
            )}
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default DiscoveryQuizModal;
